import React, { useState, useEffect, useCallback } from 'react';
import axios from '@/utils/api';
import { API } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Mail,
  Users,
  Send,
  Play,
  Settings,
  RefreshCw,
  Loader2,
  CheckCircle,
  Clock,
  AlertTriangle,
  TrendingUp,
  BarChart3,
  Trash2,
  ChevronDown,
  ChevronUp,
  Eye,
  X,
  History,
  Zap
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const OnboardingNudgeManager = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [settings, setSettings] = useState({
    enabled: false,
    step1_hours: 1,
    step2_days: 3,
    step3_days: 7,
    move_to_deletion_after_days: 14
  });
  const [showSettings, setShowSettings] = useState(false);
  const [runningAutomation, setRunningAutomation] = useState(false);
  const [sendingStep, setSendingStep] = useState(null);
  const [movingToDeletion, setMovingToDeletion] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [automationLogs, setAutomationLogs] = useState([]);
  const [showAutomationLogs, setShowAutomationLogs] = useState(false);
  
  // User list modal state
  const [showUserListModal, setShowUserListModal] = useState(false);
  const [userListModalTitle, setUserListModalTitle] = useState('');
  const [userListModalUsers, setUserListModalUsers] = useState([]);
  const [loadingUserList, setLoadingUserList] = useState(false);

  // Fetch users for a specific metric
  const fetchUsersForMetric = async (metricType) => {
    setLoadingUserList(true);
    setShowUserListModal(true);
    
    try {
      const res = await axios.get(`${API}/admin/onboarding-nudge/users-by-metric/${metricType}`);
      setUserListModalUsers(res.data.users || []);
      setUserListModalTitle(res.data.title || metricType);
    } catch (error) {
      console.error('Error fetching users for metric:', error);
      toast.error('Failed to fetch user list');
      setUserListModalUsers([]);
    } finally {
      setLoadingUserList(false);
    }
  };

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/onboarding-nudge/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching nudge stats:', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/onboarding-nudge/users`);
      setUsers(res.data);
      if (res.data.settings) {
        setSettings(res.data.settings);
      }
    } catch (error) {
      console.error('Error fetching nudge users:', error);
      toast.error('Failed to load onboarding nudge data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAutomationLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/automation-logs?automation_type=onboarding_nudge&limit=20`);
      setAutomationLogs(res.data.logs || []);
    } catch (error) {
      console.error('Error fetching automation logs:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchUsers();
    fetchAutomationLogs();
  }, [fetchStats, fetchUsers, fetchAutomationLogs]);

  const handleRunAutomation = async () => {
    setRunningAutomation(true);
    try {
      const res = await axios.post(`${API}/admin/onboarding-nudge/run-automation`);
      toast.success(res.data.message);
      await fetchStats();
      await fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run automation');
    } finally {
      setRunningAutomation(false);
    }
  };

  const handleSendStep = async (step, userIds) => {
    setSendingStep(step);
    try {
      const res = await axios.post(`${API}/admin/onboarding-nudge/send`, {
        user_ids: userIds,
        step: step
      });
      // Handle partial or complete failure
      if (res.data.success === false) {
        toast.error(res.data.message || 'Failed to send emails');
      } else if (res.data.partial) {
        toast.warning(res.data.message || `Sent ${res.data.sent_count} emails, ${res.data.failed_count} failed`);
      } else {
        toast.success(res.data.message);
      }
      await fetchStats();
      await fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingStep(null);
    }
  };

  const handleMoveToDeletion = async (userIds = null) => {
    setMovingToDeletion(true);
    try {
      const res = await axios.post(`${API}/admin/onboarding-nudge/move-to-deletion`, 
        userIds ? { user_ids: userIds } : {}
      );
      toast.success(res.data.message);
      await fetchUsers();
    } catch (error) {
      // Handle structured API errors properly
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to move users';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
      } else if (typeof errorDetail === 'object' && errorDetail !== null) {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      toast.error(errorMessage);
    } finally {
      setMovingToDeletion(false);
    }
  };

  const handleSaveSettings = async () => {
    // Frontend validation for step ordering
    if (settings.step3_days <= settings.step2_days) {
      toast.error('Step 3 days must be greater than Step 2 days');
      return;
    }
    if (settings.move_to_deletion_after_days <= settings.step3_days) {
      toast.error('Deletion days must be greater than Step 3 days');
      return;
    }
    
    setSavingSettings(true);
    try {
      await axios.post(`${API}/admin/onboarding-nudge/settings`, settings);
      toast.success('Settings saved');
      setShowSettings(false);
    } catch (error) {
      // Parse validation errors from backend
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const messages = detail.map(d => d.msg || d.message).filter(Boolean).join(', ');
        toast.error(messages || 'Failed to save settings');
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to save settings');
      }
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#df1283]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-[#FF7901] flex-shrink-0" />
            <span className="truncate">Onboarding Nudge Emails</span>
          </h2>
          <p className="text-xs sm:text-sm text-white/60 mt-1">
            Automatically remind users who signed up but have not completed onboarding
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 flex-wrap">
          {/* Automation Status Indicator */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
            settings.enabled 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
          }`}>
            <Zap className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Auto:</span> {settings.enabled ? 'ON' : 'OFF'}
          </div>
          
          <Button
            onClick={() => setShowAutomationLogs(!showAutomationLogs)}
            variant="outline"
            size="sm"
            className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30 text-xs sm:text-sm"
            data-testid="automation-logs-btn"
          >
            <History className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Logs</span>
          </Button>
          <Button
            onClick={() => setShowSettings(!showSettings)}
            variant="outline"
            size="sm"
            className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30 text-xs sm:text-sm"
            data-testid="nudge-settings-btn"
          >
            <Settings className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Settings</span>
          </Button>
          <Button
            onClick={handleRunAutomation}
            disabled={runningAutomation}
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white text-xs sm:text-sm"
            data-testid="run-nudge-automation-btn"
          >
            {runningAutomation ? (
              <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 sm:mr-2" />
            )}
            <span className="hidden sm:inline">Run Automation Now</span>
            <span className="sm:hidden">Run</span>
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-[#4A0A66]/20 border border-[#4A0A66] rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#0FFFE2]" />
            Automation Settings
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm text-white/70 mb-2">Step 1 (Hours after signup)</label>
              <input
                type="number"
                min="1"
                max="168"
                value={settings.step1_hours}
                onChange={(e) => setSettings({...settings, step1_hours: Math.min(168, Math.max(1, parseInt(e.target.value) || 1))})}
                className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white"
                data-testid="step1-hours-input"
              />
              <p className="text-xs text-white/40 mt-1">1-168 hours (max 1 week)</p>
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-2">Step 2 (Days after signup)</label>
              <input
                type="number"
                min="1"
                max="30"
                value={settings.step2_days}
                onChange={(e) => setSettings({...settings, step2_days: Math.min(30, Math.max(1, parseInt(e.target.value) || 3))})}
                className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white"
                data-testid="step2-days-input"
              />
              <p className="text-xs text-white/40 mt-1">1-30 days</p>
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-2">Step 3 (Days after signup)</label>
              <input
                type="number"
                min="1"
                max="60"
                value={settings.step3_days}
                onChange={(e) => setSettings({...settings, step3_days: Math.min(60, Math.max(1, parseInt(e.target.value) || 7))})}
                className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white"
                data-testid="step3-days-input"
              />
              <p className="text-xs text-white/40 mt-1">1-60 days</p>
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-2">Move to deletion after (days)</label>
              <input
                type="number"
                min="7"
                max="365"
                value={settings.move_to_deletion_after_days}
                onChange={(e) => setSettings({...settings, move_to_deletion_after_days: Math.min(365, Math.max(7, parseInt(e.target.value) || 14))})}
                className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white"
                data-testid="deletion-days-input"
              />
              <p className="text-xs text-white/40 mt-1">7-365 days</p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({...settings, enabled: checked})}
              />
              <span className="text-white/80">Enable automatic sending</span>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowSettings(false)}
                variant="outline"
                className="border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="bg-[#0FFFE2] text-black hover:bg-[#0FFFE2]/80"
              >
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Settings'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Automation Logs Panel */}
      {showAutomationLogs && (
        <div className="bg-[#4A0A66]/20 border border-[#4A0A66] rounded-xl p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <History className="w-5 h-5 text-[#0FFFE2]" />
              Automation Run History
            </h3>
            <Button
              onClick={() => {
                fetchAutomationLogs();
                toast.success('Logs refreshed');
              }}
              variant="outline"
              size="sm"
              className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="bg-black/20 rounded-lg p-2 sm:p-3 mb-4">
            <div className="flex items-center gap-2 text-sm text-white/70">
              <Zap className="w-4 h-4 text-[#0FFFE2]" />
              <span>
                Background automation runs every <strong className="text-[#0FFFE2]">hour</strong> when enabled. 
                External cron can also trigger via <code className="text-xs bg-black/30 px-1 py-0.5 rounded">/api/internal/cron/onboarding-nudge</code>
              </span>
            </div>
          </div>

          {automationLogs.length === 0 ? (
            <div className="text-center py-8 text-white/50">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No automation runs yet</p>
              <p className="text-xs mt-1">Runs will appear here when automation is enabled and triggered</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {automationLogs.map((log, idx) => (
                <div 
                  key={idx} 
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    log.status === 'completed' 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-red-500/10 border-red-500/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {log.status === 'completed' ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                    )}
                    <div>
                      <div className="text-sm text-white font-medium">
                        {log.trigger === 'cron_endpoint' ? 'Cron Triggered' : 'Background Automation'}
                      </div>
                      <div className="text-xs text-white/60">
                        {log.run_at ? formatDistanceToNow(new Date(log.run_at), { addSuffix: true }) : 'Unknown time'}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {log.status === 'completed' ? (
                      <div className="text-sm font-medium text-green-400">
                        {log.total_sent || 0} emails sent
                      </div>
                    ) : (
                      <div className="text-sm font-medium text-red-400">
                        Failed
                      </div>
                    )}
                    {log.results && (
                      <div className="text-xs text-white/50">
                        S1: {log.results.step1 || 0} | S2: {log.results.step2 || 0} | S3: {log.results.step3 || 0}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div 
            className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50 cursor-pointer hover:bg-[#4A0A66]/50 hover:border-[#df1283]/50 transition-all"
            onClick={() => fetchUsersForMetric('incomplete_onboarding')}
          >
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-[#df1283]" />
              <span className="text-xs text-white/60">Incomplete Onboarding</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.total_incomplete_onboarding}</div>
            <div className="text-xs text-white/40 mt-1">Click to view users</div>
          </div>
          
          <div 
            className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50 cursor-pointer hover:bg-[#4A0A66]/50 hover:border-[#FF7901]/50 transition-all"
            onClick={() => fetchUsersForMetric('emails_sent')}
          >
            <div className="flex items-center gap-2 mb-2">
              <Send className="w-4 h-4 text-[#FF7901]" />
              <span className="text-xs text-white/60">Emails Sent</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.emails_sent?.total || 0}</div>
            <div className="text-xs text-white/40 mt-1">Click to view users</div>
          </div>
          
          <div className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-[#0FFFE2]" />
              <span className="text-xs text-white/60">Open Rate</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.open_rate}%</div>
          </div>
          
          <div 
            className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50 cursor-pointer hover:bg-[#4A0A66]/50 hover:border-green-500/50 transition-all"
            onClick={() => fetchUsersForMetric('completed_after_nudge')}
          >
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-xs text-white/60">Completed After Nudge</span>
            </div>
            <div className="text-2xl font-bold text-green-400">{stats.completed_after_nudge}</div>
            <div className="text-xs text-white/40 mt-1">Click to view users</div>
          </div>
          
          <div className="bg-[#4A0A66]/30 rounded-xl p-4 border border-[#4A0A66]/50">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-white/60">Conversion Rate</span>
            </div>
            <div className="text-2xl font-bold text-amber-400">{stats.conversion_rate}%</div>
          </div>
        </div>
      )}

      {/* Step Queues */}
      {users && (
        <div className="space-y-4">
          {/* Step 1 Queue */}
          <StepSection
            step={1}
            title="Step 1: Complete Your Setup!"
            description={`Users who signed up ${settings.step1_hours}+ hour(s) ago, never nudged`}
            users={users.pending_step1 || []}
            waitingUsers={users.waiting_for_step1 || []}
            waitingLabel={`Waiting (signed up < ${settings.step1_hours} hour${settings.step1_hours !== 1 ? 's' : ''} ago)`}
            color="#df1283"
            isExpanded={expandedSection === 'step1'}
            onToggle={() => toggleSection('step1')}
            onSend={(userIds) => handleSendStep(1, userIds)}
            sending={sendingStep === 1}
            showHours={true}
          />
          
          {/* Step 2 Queue */}
          <StepSection
            step={2}
            title="Step 2: Don't Miss Out!"
            description={`Users who got Step 1, signed up ${settings.step2_days}+ days ago`}
            users={users.pending_step2 || []}
            waitingUsers={users.waiting_for_step2 || []}
            waitingLabel={`Waiting (got Step 1, < ${settings.step2_days} days since signup)`}
            color="#FF7901"
            isExpanded={expandedSection === 'step2'}
            onToggle={() => toggleSection('step2')}
            onSend={(userIds) => handleSendStep(2, userIds)}
            sending={sendingStep === 2}
          />
          
          {/* Step 3 Queue */}
          <StepSection
            step={3}
            title="Step 3: Last Chance!"
            description={`Users who got Step 2, signed up ${settings.step3_days}+ days ago`}
            users={users.pending_step3 || []}
            waitingUsers={users.waiting_for_step3 || []}
            waitingLabel={`Waiting (got Step 2, < ${settings.step3_days} days since signup)`}
            color="#0FFFE2"
            isExpanded={expandedSection === 'step3'}
            onToggle={() => toggleSection('step3')}
            onSend={(userIds) => handleSendStep(3, userIds)}
            sending={sendingStep === 3}
          />
          
          {/* Ready for Deletion */}
          {users.ready_for_deletion_flow && users.ready_for_deletion_flow.length > 0 && (
            <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Ready for Deletion Flow</h3>
                    <p className="text-sm text-white/60">
                      {users.ready_for_deletion_flow.length} users completed all nudges but still have not onboarded
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => handleMoveToDeletion()}
                  disabled={movingToDeletion}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                  data-testid="move-to-deletion-btn"
                >
                  {movingToDeletion ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Move All to Deletion
                </Button>
              </div>
              
              {users.ready_for_deletion_flow.length > 0 && (
                <div className="mt-4 max-h-40 overflow-y-auto">
                  <div className="space-y-2">
                    {users.ready_for_deletion_flow.slice(0, 10).map((user) => (
                      <div key={user.user_id} className="flex items-center justify-between text-sm bg-black/20 rounded-lg px-3 py-2">
                        <div>
                          <span className="text-white">{user.first_name || 'Unknown'}</span>
                          <span className="text-white/40 ml-2">{user.email}</span>
                        </div>
                        <span className="text-red-400 text-xs">{user.days_since_signup} days ago</span>
                      </div>
                    ))}
                    {users.ready_for_deletion_flow.length > 10 && (
                      <div className="text-center text-white/40 text-sm py-2">
                        +{users.ready_for_deletion_flow.length - 10} more users
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Email Schedule Timeline */}
      <div className="bg-[#4A0A66]/20 border border-[#4A0A66] rounded-xl p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#FF7901]" />
          Email Schedule
        </h3>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex items-center gap-2 sm:gap-4 min-w-fit">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#df1283]/20 border-2 border-[#df1283] flex items-center justify-center">
                <span className="text-[#df1283] font-bold text-sm sm:text-base">1</span>
              </div>
              <span className="text-xs text-white/60 mt-2">{settings.step1_hours}h</span>
            </div>
            <div className="w-8 sm:w-16 h-0.5 bg-[#4A0A66]" />
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FF7901]/20 border-2 border-[#FF7901] flex items-center justify-center">
                <span className="text-[#FF7901] font-bold text-sm sm:text-base">2</span>
              </div>
              <span className="text-xs text-white/60 mt-2">Day {settings.step2_days}</span>
            </div>
            <div className="w-8 sm:w-16 h-0.5 bg-[#4A0A66]" />
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#0FFFE2]/20 border-2 border-[#0FFFE2] flex items-center justify-center">
                <span className="text-[#0FFFE2] font-bold text-sm sm:text-base">3</span>
              </div>
              <span className="text-xs text-white/60 mt-2">Day {settings.step3_days}</span>
            </div>
            <div className="w-8 sm:w-16 h-0.5 bg-[#4A0A66]" />
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center">
                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
              </div>
              <span className="text-xs text-white/60 mt-2">Day {settings.move_to_deletion_after_days}+</span>
            </div>
          </div>
        </div>
      </div>

      {/* User List Modal */}
      {showUserListModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A0326] border border-[#4A0A66] rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#4A0A66]">
              <h3 className="text-lg font-semibold text-white">{userListModalTitle}</h3>
              <button
                onClick={() => setShowUserListModal(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {loadingUserList ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#df1283]" />
                </div>
              ) : userListModalUsers.length === 0 ? (
                <div className="text-center py-12 text-white/40">
                  No users found
                </div>
              ) : (
                <div className="space-y-2">
                  {userListModalUsers.map((user, index) => (
                    <div 
                      key={user.user_id || index}
                      className="flex items-center justify-between bg-[#4A0A66]/20 rounded-lg px-4 py-3 hover:bg-[#4A0A66]/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#df1283]/20 flex items-center justify-center">
                          <span className="text-[#df1283] font-semibold">
                            {(user.first_name || user.name || user.email || '?')[0].toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="text-white font-medium">
                            {user.first_name && user.last_name 
                              ? `${user.first_name} ${user.last_name}`
                              : user.name || 'Unknown'}
                          </div>
                          <div className="text-sm text-white/50">{user.email}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        {user.created_at && (
                          <div className="text-xs text-white/40">
                            Signed up {formatDistanceToNow(new Date(user.created_at), { addSuffix: true })}
                          </div>
                        )}
                        {user.nudge_completed_at && (
                          <div className="text-xs text-green-400">
                            Completed {formatDistanceToNow(new Date(user.nudge_completed_at), { addSuffix: true })}
                          </div>
                        )}
                        {user.membership_tier && (
                          <div className="text-xs text-[#FF7901] mt-1">
                            {user.membership_tier.replace('_', ' ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="border-t border-[#4A0A66] p-4 flex justify-between items-center">
              <span className="text-sm text-white/40">
                {userListModalUsers.length} user{userListModalUsers.length !== 1 ? 's' : ''}
              </span>
              <Button
                variant="outline"
                onClick={() => setShowUserListModal(false)}
                className="border-[#4A0A66] text-white hover:bg-[#4A0A66]/30"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Step Section Component
const StepSection = ({ step, title, description, users, waitingUsers = [], waitingLabel = "", color, isExpanded, onToggle, onSend, sending, showHours = false }) => {
  const [selectedUsers, setSelectedUsers] = useState([]);

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.user_id));
    }
  };

  const handleSendSelected = () => {
    if (selectedUsers.length > 0) {
      onSend(selectedUsers);
      setSelectedUsers([]);
    }
  };

  const handleSendAll = () => {
    onSend(users.map(u => u.user_id));
  };

  return (
    <div 
      className="bg-[#2D063E] border rounded-xl overflow-hidden"
      style={{ borderColor: `${color}40` }}
    >
      <div 
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 cursor-pointer hover:bg-[#4A0A66]/20 gap-3"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        data-testid={`step${step}-section-toggle`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${color}20` }}
          >
            <span className="font-bold" style={{ color }}>{step}</span>
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-white truncate">{title}</h3>
            <p className="text-sm text-white/60 truncate">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap justify-end">
          {/* Ready to send badge */}
          <div 
            className="px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap"
            style={{ backgroundColor: `${color}20`, color }}
            data-testid={`step${step}-ready-badge`}
          >
            {users.length} ready
          </div>
          {/* Waiting badge */}
          {waitingUsers.length > 0 && (
            <div 
              className="px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-amber-500/20 text-amber-400 flex items-center gap-1 whitespace-nowrap"
              title={waitingLabel}
              data-testid={`step${step}-waiting-badge`}
            >
              <Clock className="w-3 h-3" />
              {waitingUsers.length} waiting
            </div>
          )}
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleSendAll();
            }}
            disabled={sending || users.length === 0}
            size="sm"
            className="text-white text-xs sm:text-sm"
            style={{ backgroundColor: users.length > 0 ? color : '#666', opacity: users.length === 0 ? 0.5 : 1 }}
            data-testid={`send-step${step}-all-btn`}
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4 mr-1" /> <span className="hidden xs:inline">Send All</span><span className="xs:hidden">Send</span>
              </>
            )}
          </Button>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-white/40 flex-shrink-0" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white/40 flex-shrink-0" />
          )}
        </div>
      </div>
      
      {isExpanded && users.length > 0 && (
        <div className="border-t border-[#4A0A66]/50 p-4">
          <div className="flex items-center justify-between mb-3">
            <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedUsers.length === users.length}
                onChange={handleSelectAll}
                className="rounded border-white/20 bg-black/30"
              />
              Select All ({users.length})
            </label>
            {selectedUsers.length > 0 && (
              <Button
                onClick={handleSendSelected}
                disabled={sending}
                size="sm"
                variant="outline"
                className="border-white/20 text-white"
              >
                <Send className="w-4 h-4 mr-1" /> Send to Selected ({selectedUsers.length})
              </Button>
            )}
          </div>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {users.map((user) => (
              <div 
                key={user.user_id}
                className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.user_id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers([...selectedUsers, user.user_id]);
                      } else {
                        setSelectedUsers(selectedUsers.filter(id => id !== user.user_id));
                      }
                    }}
                    className="rounded border-white/20 bg-black/30"
                  />
                  <div>
                    <span className="text-white text-sm">{user.first_name || 'Unknown'} {user.last_name || ''}</span>
                    <span className="text-white/40 text-sm ml-2">{user.email}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-white/40">
                    {showHours 
                      ? `${user.hours_since_signup || 0} hours ago`
                      : `${user.days_since_signup} days ago`
                    }
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {isExpanded && users.length === 0 && waitingUsers.length === 0 && (
        <div className="border-t border-[#4A0A66]/50 p-8 text-center">
          <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
          <p className="text-white/60">No users pending for this step</p>
        </div>
      )}
      
      {/* Waiting Users Section */}
      {isExpanded && waitingUsers.length > 0 && (
        <div className="border-t border-amber-500/30 bg-amber-500/5 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium text-amber-400">{waitingLabel}</span>
            <span className="text-xs text-white/40">({waitingUsers.length} users)</span>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {waitingUsers.map((user) => (
              <div 
                key={user.user_id}
                className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <Clock className="w-3 h-3 text-amber-400/50" />
                  <div>
                    <span className="text-white/70 text-sm">{user.first_name || 'Unknown'} {user.last_name || ''}</span>
                    <span className="text-white/30 text-sm ml-2">{user.email}</span>
                  </div>
                </div>
                <div className="text-xs text-amber-400">
                  {user.days_until_step2 > 0 && step === 2 && `${user.days_until_step2} day(s) left`}
                  {user.days_until_step3 > 0 && step === 3 && `${user.days_until_step3} day(s) left`}
                  {user.hours_until_eligible && step === 1 && `${user.hours_until_eligible}h left`}
                  {!user.days_until_step2 && !user.days_until_step3 && !user.hours_until_eligible && 'Soon'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingNudgeManager;
